/**
 * TopicAssetCard — one asset tile in the "View by Topic" swimlane (Figma node
 * 2383:49554). Visually IDENTICAL to the by-Industry AssetCard (same pastel
 * maturity fill via --card-accent = MATURITY_OVERVIEW_COLOR, same GREEN new bar
 * for isNew assets, same hover, same size) so a card looks the same in either
 * view. Body mirrors AssetCard
 * (name + 八要素完成度%) and additionally shows the sub-topic it's linked to
 * under this topic row (topicLinks[].subTopicName).
 *   - click → navigate to /ivt/assets/:assetId
 *
 * Rendered inside the same .ivt-assets-matrix grid as AssetsIndustryMatrix, so it
 * needs no sizing class of its own — it fills its grid cell exactly like a
 * by-Industry AssetCard (identical width + equal-height rows).
 */
import { useNavigate } from 'react-router-dom';
import { MATURITY_OVERVIEW_COLOR } from '../../../utils/maturity';
import type { AssetSummary } from '../../../client/IvtHubClient';
import { isNew } from '../utils/assetGrouping';

const FALLBACK_COLOR = '#eceded';

/** The sub-topic this asset sits under (first topicLink that carries a name). */
function subTopicOf(a: AssetSummary): string | undefined {
  return (a.topicLinks ?? []).find((l) => l.subTopicName)?.subTopicName ?? undefined;
}

export default function TopicAssetCard({ asset }: { asset: AssetSummary }) {
  const navigate = useNavigate();
  const accent = asset.maturity ? MATURITY_OVERVIEW_COLOR[asset.maturity] : FALLBACK_COLOR;
  const showNewBar = isNew(asset);
  const label = asset.name ?? asset.useCase ?? asset.assetId ?? '—';
  const completeness = asset.elementCompleteness ?? 0;
  const subTopic = subTopicOf(asset);

  return (
    <button
      type="button"
      className="ivt-asset-card"
      style={{ ['--card-accent' as string]: accent }}
      onClick={() => asset.assetId && navigate(`/ivt/assets/${asset.assetId}`)}
      aria-label={`Asset ${label}`}
    >
      {showNewBar && <span className="ivt-asset-card__newbar" aria-label="New asset" />}
      <span className="ivt-asset-card__name">{label}</span>
      {completeness > 0 && (
        <span className="ivt-asset-card__completeness">
          八要素完成度：{Math.round((completeness / 8) * 100)}%
        </span>
      )}
      {subTopic && <span className="ivt-asset-card__uc">{subTopic}</span>}
    </button>
  );
}
